import { IonCol, IonRouterLink, IonRow, IonSearchbar } from '@ionic/react';
import { DataTable } from 'src/components/shared/DataTable';
import { DataTableHeader } from 'src/components/shared/DataTable/interfaces/DataTableHeader';
import { DataTableRow } from 'src/components/shared/DataTable/interfaces/DataTableRow';
import { useApi } from 'src/hooks/useApi';
import { useUtils } from 'src/hooks/useUtils';
import { useEffect, useState, useCallback } from 'react';
import { AppUserSelf } from 'src/interfaces/AppUserSelf';
import { useSelector } from 'react-redux';
import { DataTablePagination } from 'src/components/shared/DataTable/interfaces/DataTablePagination';
import { useDebounce, useDebounceWithIdle } from 'src/hooks/useDebounce';


const headers: DataTableHeader[] = [
  { text: 'Account', key: 'account_name', sortable: true },
  { text: 'Total Sales', key: 'account_total', align: 'right', sortable: true },
];

interface Customer {
  account_name: string;
  account_number: string;
  account_total: number;
}

interface ApiResponse {
  result: Customer[];
  total: number;
}

export const TableTopCustomers: React.FC<{ minDate: string | null }> = ({
  minDate,
}) => {
  const api = useApi();
  const utils = useUtils();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [pagination, setPagination] = useState<DataTablePagination>({ limit: 10, offset: 0, page: 0 });
  const [totalCount, setTotalCount] = useState<number>(0);
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [orderByDesc, setOrderByDesc] = useState<boolean>();
  const [search, setSearch] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string | null>(null);

  const authUser: AppUserSelf | null = useSelector(
    (state: any) => state.auth.user
  );

  const fetchTopCustomers = useCallback(async () => {
    if (!authUser || !pagination) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`supplier/${authUser.organization_id}/top_customers`, {
        min_date: minDate,
        limit: pagination.limit,
        offset: pagination.offset,
        order_by: orderBy || null,
        order_by_desc: orderByDesc,
        search: search || undefined,
      });

      if (response.status === 200) {
        if (response.data?.result) {
          setCustomers(response.data.result);
          setTotalCount(response.data.total);
        } else {
          console.warn('Received null result from top customers API');
          setCustomers([]);
          setTotalCount(0);
        }
      }
    } catch (error) {
      console.error('Failed to fetch top customers:', error);
      setCustomers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [api, authUser, minDate, orderBy, orderByDesc, pagination, search]);

  const getTopCustomers = useDebounce(fetchTopCustomers, 300);

  useEffect(() => {
    getTopCustomers();
  }, [minDate, pagination.limit, pagination.offset, orderBy, orderByDesc, search, authUser?.organization_id]);

  const updateSearch = useCallback((value: string) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, offset: 0, page: 0 }));
  }, []);

  const handleSearch = useDebounceWithIdle(updateSearch, 500);

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
    handleSearch(value);
  };

  return (
    <DataTable
      title="Top Customers"
      headers={headers}
      loading={loading}
      onPaginationChange={setPagination}
      orderBy={orderBy}
      orderByDesc={orderByDesc}
      setOrderBy={setOrderBy}
      setOrderByDesc={setOrderByDesc}
      itemsPerPageOptions={[5, 10, 25, 50]}
      itemsPerPage={pagination.limit}
      totalCount={totalCount}
      pagination={pagination}
      search={false}
      rows={customers.map((customer, index): DataTableRow => ({
        item: customer,
        key: `${customer.account_number}-${index}`,
        columns: [
          {
            header: 'account_name',
            content: (
              <div className="itemCol">
                <IonRouterLink className="font-size-large" color="secondary">
                  {customer.account_name}
                </IonRouterLink>
                <p>{customer.account_number}</p>
              </div>
            ),
          },
          {
            header: 'account_total',
            content: (
              <p className="text-color-black font-size-default font-weight-500">
                {utils.toUsd(customer.account_total, 0)}
              </p>
            ),
          },
        ],
      }))}
    >
      <IonRow>
        <IonCol size="12" sizeSm="8" offsetSm="4">
          <IonSearchbar
            value={searchInput}
            onIonChange={(e: any) => handleSearchInput(e.detail.value as string)}
            debounce={0}
            className="ion-text-left"
          />
        </IonCol>
      </IonRow>
    </DataTable>
  );
};
